// askSmart-ui/src/pages/TOSP/components/TaxBillUploadModal.tsx
import React, { useEffect, useRef, useState } from "react";
import { formatCurrency } from "../format";
import {
  applyIngest,
  IngestPlan,
  ParsedBill,
  planIngest,
  readTaxBill,
} from "../taxBillIngest";
import { TAX_BILL_LABELS, TaxRollRecord } from "../types";

interface TaxBillUploadModalProps {
  /**
   * The chosen bill, or null when closed.
   *
   * The file is picked by the toolbar button before this opens — a modal whose
   * only content is a second button to press is a step that asks nothing.
   */
  file: File | null;
  /** The year tab this was opened from — checked against the bill's own year. */
  activeYear: number;
  register: TaxRollRecord[];
  onClose: () => void;
  onApply: (rolls: TaxRollRecord[], billLabel: string) => Promise<void>;
}

/** Decorative — the button's own text is what a screen reader announces. */
const Spinner = () => <span className="tosp-spinner" aria-hidden="true" />;

const percent = (value: number): string =>
  `${value > 0 ? "+" : ""}${(value * 100).toFixed(1)}%`;

/** yyyy-mm-dd → 20 Mar 2026, without pulling in a date library. */
const readableDate = (iso: string): string => {
  const [year, month, day] = iso.split("-");
  const months = "Jan Feb Mar Apr May Jun Jul Aug Sep Oct Nov Dec".split(" ");
  return `${Number(day)} ${months[Number(month) - 1] || month} ${year}`;
};

// Uploading a tax bill.
//
// One button rather than "Add Interim" / "Add Final": the bill states its own
// type and year, so asking the admin to classify it only creates a way to get
// it wrong. What it does ask for is confirmation — parse first, show what was
// found and what it would change, then write. A bill that parsed wrongly is
// worse than one that was never uploaded, because nothing about it errors.
export default function TaxBillUploadModal({
  file,
  activeYear,
  register,
  onClose,
  onApply,
}: TaxBillUploadModalProps) {
  const fileInput = useRef<HTMLInputElement>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [bill, setBill] = useState<ParsedBill | null>(null);
  const [plan, setPlan] = useState<IngestPlan | null>(null);
  /** Whatever is being read now — the prop's file, or a retry picked here. */
  const [sourceName, setSourceName] = useState("");

  const reset = () => {
    setBill(null);
    setPlan(null);
    setError(null);
    setSourceName("");
    if (fileInput.current) fileInput.current.value = "";
  };

  const close = () => {
    reset();
    onClose();
  };

  const handleFile = async (file: File) => {
    setBusy(true);
    setError(null);
    setSourceName(file.name);
    try {
      const bytes = new Uint8Array(await file.arrayBuffer());
      const parsed = await readTaxBill(bytes);

      if (parsed.levies.length === 0) {
        // Distinguish "nothing readable" from "read it but couldn't find the
        // figures" — they need different things done about them, and one
        // message for both sends you looking at the wrong end of the problem.
        setError(
          parsed.skippedPages === 0
            ? "That PDF has no pages this could read."
            : `Read ${parsed.skippedPages} page${
                parsed.skippedPages === 1 ? "" : "s"
              } but found no roll numbers or levy totals on any of them. If it's a scan there's no text to read; otherwise it may be a layout this hasn't seen.`,
        );
        return;
      }
      if (!parsed.type || !parsed.year) {
        setError(
          "Couldn't tell whether that's an Interim or a Final bill, or which year it covers. It may be a format this hasn't seen.",
        );
        return;
      }

      setBill(parsed);
      setPlan(planIngest(register, parsed));
    } catch (err) {
      console.error("[TOSP] tax bill parse failed:", err);
      setError("Couldn't read that PDF. It may be password-protected or damaged.");
    } finally {
      setBusy(false);
    }
  };

  // Reading starts the moment the file arrives, so the first thing shown is
  // either progress or the answer — never a prompt for what was already chosen.
  // Declared after handleFile so the effect isn't closing over a name that
  // hasn't been assigned yet.
  useEffect(() => {
    if (file) handleFile(file);
    else reset();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [file]);

  if (!file) return null;

  const apply = async () => {
    if (!bill || !plan || !bill.type || !bill.year) return;
    setBusy(true);
    try {
      const label = `${bill.year} ${TAX_BILL_LABELS[bill.type]}`;
      await onApply(applyIngest(plan, bill), label);
      close();
    } catch {
      /* surfaced by the provider */
    } finally {
      setBusy(false);
    }
  };

  const wrongYear = bill?.year !== undefined && bill?.year !== null && bill.year !== activeYear;

  return (
    <div className="smrtdocs-modal-overlay">
      <div className="smrtdocs-modal tosp-modal-wide">
        <div className="smrtdocs-modal-header">
          <h2 style={{ margin: 0, fontSize: "1rem" }}>Upload Tax Bill</h2>
        </div>
        <div className="smrtdocs-modal-body">
          {!plan || !bill ? (
            <>
              {/* Reading 152 pages takes a moment, and the file name is the
                  only confirmation that the right bill was picked. */}
              {busy && (
                <p className="tosp-field-hint" style={{ marginTop: 0 }}>
                  <Spinner />
                  Reading {sourceName || file.name}…
                </p>
              )}
              {error && <p className="tosp-field-error">{error}</p>}

              {/* Kept for the retry: a bill that didn't parse is usually the
                  wrong file, and re-picking here beats closing and starting
                  again. */}
              <input
                ref={fileInput}
                type="file"
                accept="application/pdf"
                style={{ display: "none" }}
                onChange={(e) => {
                  const chosen = e.target.files?.[0];
                  if (chosen) handleFile(chosen);
                }}
              />
              {!busy && (
                <button
                  type="button"
                  className="tosp-btn-blue"
                  onClick={() => fileInput.current?.click()}
                >
                  Choose another PDF
                </button>
              )}
            </>
          ) : (
            <>
              <div className="tosp-ingest-head">
                <strong>
                  {bill.year} {bill.type ? TAX_BILL_LABELS[bill.type] : ""} Tax Bill
                </strong>
                <span>
                  {bill.levies.length} roll{bill.levies.length === 1 ? "" : "s"} ·{" "}
                  {formatCurrency(plan.total)}
                  {/* A Final charges the year's tax less what the Interim
                      already took, so what it bills is about half what it's
                      headed with. Both are shown rather than leaving the
                      smaller figure to look like a misread. */}
                  {plan.annualTotal !== null
                    ? ` charged · ${formatCurrency(plan.annualTotal)} for the year`
                    : ""}
                  {bill.mailingDate ? ` · issued ${readableDate(bill.mailingDate)}` : ""}
                </span>
              </div>

              {wrongYear && (
                <p className="tosp-ingest-flag tosp-ingest-flag-warn">
                  This is a {bill.year} bill, but you're on the {activeYear} tab. Applying it
                  records it against {bill.year}, where it belongs.
                </p>
              )}

              {plan.alreadyRecorded && (
                <p className="tosp-ingest-flag tosp-ingest-flag-warn">
                  This bill has already been recorded. Applying it again will overwrite the
                  current figures.
                </p>
              )}

              <ul className="tosp-ingest-list">
                <li>
                  <strong>{plan.matched.length}</strong> rolls matched — levies and instalments
                  will be recorded
                </li>
                {plan.unknown.length > 0 && (
                  <li className="tosp-ingest-flag-warn">
                    <strong>{plan.unknown.length}</strong> rolls on this bill are not in the
                    register — they'll be skipped. Add them on the Register tab, then upload
                    again.
                  </li>
                )}
                {plan.missing.length > 0 && (
                  <li className="tosp-ingest-flag-warn">
                    <strong>{plan.missing.length}</strong> rolls in the register are absent from
                    this bill — sold, severed, or merged?
                  </li>
                )}
                {/* Mostly an unpaid Interim instalment being re-presented, so
                    it's held beside the levy rather than added to it — the tax
                    in it is already charged once. Any penalty inside it is a
                    real extra cost and isn't allocated to a tenant yet. */}
                {plan.arrears.length > 0 && (
                  <li>
                    <strong>{plan.arrears.length}</strong> carry a past-due balance of{" "}
                    {formatCurrency(plan.arrears.reduce((sum, a) => sum + a.amount, 0))},
                    recorded on the roll — unpaid tax the {bill.year} Interim already charged,
                    plus penalty
                  </li>
                )}
                {plan.swings.length > 0 && (
                  <li>
                    <strong>{plan.swings.length}</strong> moved more than 10% against last
                    year's {bill.type ? TAX_BILL_LABELS[bill.type] : ""} — worth a look
                  </li>
                )}
              </ul>

              {plan.swings.length > 0 && (
                <div className="tosp-ingest-detail">
                  {plan.swings.slice(0, 8).map(({ roll, from, to, percent: pct }) => (
                    <div key={roll.id}>
                      {roll.rollNumber} · {formatCurrency(from)} → {formatCurrency(to)}{" "}
                      <span className={pct > 0 ? "tosp-roll-unassigned" : undefined}>
                        {percent(pct)}
                      </span>
                    </div>
                  ))}
                  {plan.swings.length > 8 && <div>…and {plan.swings.length - 8} more</div>}
                </div>
              )}

              {plan.unknown.length > 0 && (
                <div className="tosp-ingest-detail">
                  {plan.unknown.slice(0, 8).map((parsed) => (
                    <div key={parsed.rollNumber}>
                      {parsed.rollNumber} · {formatCurrency(parsed.levy)} — not in register
                    </div>
                  ))}
                  {plan.unknown.length > 8 && <div>…and {plan.unknown.length - 8} more</div>}
                </div>
              )}
            </>
          )}

          <div style={{ display: "flex", justifyContent: "space-between", marginTop: 16 }}>
            <button type="button" className="btn-cancel" onClick={close}>
              Cancel
            </button>
            {plan && (
              <button
                type="button"
                className="btn-save"
                disabled={busy || plan.matched.length === 0}
                onClick={apply}
              >
                {busy ? (
                  <>
                    <Spinner />
                    Saving…
                  </>
                ) : (
                  `Apply to ${plan.matched.length} rolls`
                )}
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
